import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { fabric } from 'fabric';
import LineIcon from '../images/Icon20_Line.svg';
import {
  setCursorTooltip,
  updateTool,
} from '../../../redux/actions/canvas_editor';
import Button from './Toolbar/Button';
import { connect } from 'react-redux';
import { getThicknessPx, getDrawingBounds } from '../helpers/fabricHelpers';
import { __ } from '../../../core/utils';

const Line = ({
  canvas_editor: { viewer, color, selectedThickness, fabricCanvases },
  isActive,
  source: { width, height },
  sourceType,
  user_id,
  user_full_name,
}) => {
  const [state, setState] = useState({
    line: null,
    isMouseDown: false,
    startLeft: null,
    startTop: null,
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (!isActive || !fabricCanvases) return;

    setCursorTooltip(__('canvas.tooltip.line.start'));

    const disposers = [];

    Object.entries(fabricCanvases).forEach(([pageNumber, canvas]) => {
      canvas.defaultCursor = 'crosshair';
      canvas.hoverCursor = 'crosshair';
      canvas.selection = false;
      canvas.skipTargetFind = true;

      canvas.getObjects().forEach((obj) => {
        obj.selectable = false;
        obj.evented = false;
      });

      const onPointerDown = () => {
        viewer?.setMouseNavEnabled(false);
      };

      canvas.upperCanvasEl.addEventListener('pointerdown', onPointerDown);

      function onMouseDown(options) {
        if (options.target) return;

        const rawPointer = canvas.getPointer(options.e);
        const bounds = getDrawingBounds(canvas, { width, height }, sourceType);
        const pointer = {
          x: Math.max(0, Math.min(bounds.width, rawPointer.x)),
          y: Math.max(0, Math.min(bounds.height, rawPointer.y)),
        };
        const pdfScaleFactor =
          sourceType === 'pdf' ? canvas?.pdfScaleFactor || 1 : 1;
        const strokeWidth =
          getThicknessPx(selectedThickness.key) * pdfScaleFactor;

        const line = new fabric.Line(
          [pointer.x, pointer.y, pointer.x, pointer.y],
          {
            stroke: color.hex,
            strokeWidth,
            hasBorders: false,
            hasControls: false,
            objectType: 'line',
            selectable: false,
            strokeUniform: true,
            //hoverCursos: 'default',
            hasBorders: false,
            hasControls: false,
            startLeft: pointer.x,
            startTop: pointer.y,
            userID: user_id,
            userFullName: user_full_name,
            pageNumber,
          }
        );

        canvas.add(line);

        setState((s) => ({
          ...s,
          line,
          isMouseDown: true,
          startLeft: pointer.x,
          startTop: pointer.y,
        }));
      }

      function onMouseMove(options) {
        if (!stateRef.current.isMouseDown || !stateRef.current.line) return;

        const rawPointer = canvas.getPointer(options.e);
        const bounds = getDrawingBounds(canvas, { width, height }, sourceType);
        const pointer = {
          x: Math.max(0, Math.min(bounds.width, rawPointer.x)),
          y: Math.max(0, Math.min(bounds.height, rawPointer.y)),
        };

        stateRef.current.line.set({ x2: pointer.x, y2: pointer.y });
        stateRef.current.line.setCoords();
        canvas.requestRenderAll();

        setCursorTooltip(__('canvas.tooltip.line.drawing'));
      }

      function onMouseUp() {
        if (!stateRef.current.isMouseDown || !stateRef.current.line) return;

        const { line } = stateRef.current;

        line.set({
          startLeft: line.left,
          startTop: line.top,
          //selectable: true,
        });

        line.setCoords();

        setState((s) => ({
          ...s,
          line: null,
          isMouseDown: false,
          startLeft: null,
          startTop: null,
        }));

        viewer?.setMouseNavEnabled(true);
        canvas.discardActiveObject();
        canvas.requestRenderAll();

        window.requestAnimationFrame(() => {
          updateTool({ tool: 'select' });
        });
      }

      canvas.on('mouse:down', onMouseDown);
      canvas.on('mouse:move', onMouseMove);
      canvas.on('mouse:up', onMouseUp);

      disposers.push(() => {
        if (canvas?.upperCanvasEl) {
          canvas.upperCanvasEl.removeEventListener(
            'pointerdown',
            onPointerDown
          );
        }

        canvas.off('mouse:down', onMouseDown);
        canvas.off('mouse:move', onMouseMove);
        canvas.off('mouse:up', onMouseUp);
        canvas.skipTargetFind = false;
      });
    });

    return () => disposers.forEach((dispose) => dispose());
  }, [
    isActive,
    color,
    selectedThickness,
    fabricCanvases,
    width,
    height,
    sourceType,
  ]);

  const toggleTool = () => {
    updateTool({ tool: 'line' });
  };

  return (
    <Button
      id='line'
      label={__('canvas.toolbar.line')}
      icon={LineIcon}
      active={isActive}
      onClick={toggleTool}
      extraClass={['roundedUp']}
    />
  );
};

Line.propTypes = {
  isActive: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  canvas_editor: state.canvas_editor,
  user_id: state.auth.id,
  user_full_name: state.auth.firstname + ' ' + state.auth.lastname,
});

export default connect(mapStateToProps, null)(Line);
